import { CalibrationType } from "@/shared/enums/CalibrationType";
import { SessionType } from "@/shared/enums/SessionType";
import { AUGGIEAVAILABLE } from "@/main";
import { CalibrationSessionDto } from "@/shared/models/CalibrationSessionDto";
import CameraViewerConfiguration from "@/shared/models/CameraViewerConfiguration";
import { PackageDto } from "@/shared/models/PackageDto";
import { VehicleDto } from "@/shared/models/VehicleDto";
import { MarkerType } from "@/shared/enums/MarkerType";
import BatteryInfoDto from "@/shared/models/BatteryInfoDto";
import TemperatureInfoDto from "@/shared/models/TemperatureInfoDto";
import CpuInfoDto from "@/shared/models/CpuInfoDto";
import CellularSignalDto from "@/shared/models/CellularSignalDto";
import CellularInfoDto from "@/shared/models/CellularInfoDto";

// Consecutive failed camera starts before the operator is told rather than retried at, and before a
// technician-controlled session stops advancing back into the camera page on its own.
export const CAMERASTARTFAILURELIMIT = 3;

interface EnvironmentConfig {
    WebApiUrl: string;
    SignalRUrl: string;
    CompanionUrl: string;
}

// Keys of ENVIRONMENT_CONFIG. The tier half is what the device client reports, so the key is built
// by concatenation and has to be narrowed back to this union before it can index the table.
type EnvKey = "DEV" | "QAT" | "UAT" | "STG" | "PRD" | "UAT_AUS" | "PRD_AUS";

const ENVIRONMENT_CONFIG: Record<EnvKey, EnvironmentConfig> = {
    DEV: {
        WebApiUrl: "https://localhost:44359/",
        SignalRUrl: "http://localhost:7174/api",
        CompanionUrl: "https://localhost:44343/",
    },
    QAT: {
        WebApiUrl: "https://api-qat.auggie.tech/device",
        SignalRUrl: "https://api-qat.auggie.tech/signalr",
        CompanionUrl: "https://companion-qat.auggie.tech/",
    },
    UAT: {
        WebApiUrl: "https://api-uat.auggie.tech/device",
        SignalRUrl: "https://api-uat.auggie.tech/signalr",
        CompanionUrl: "https://companion-uat.auggie.tech/",
    },
    STG: {
        WebApiUrl: "https://api-stg.auggie.tech/device",
        SignalRUrl: "https://api-stg.auggie.tech/signalr",
        CompanionUrl: "https://companion-stg.auggie.tech/",
    },
    PRD: {
        WebApiUrl: "https://api.auggie.tech/device",
        SignalRUrl: "https://api.auggie.tech/signalr",
        CompanionUrl: "https://companion.auggie.tech/",
    },
    UAT_AUS: {
        WebApiUrl: "https://api-uat.auggie.airprodiag.com.au/device",
        SignalRUrl: "https://api-uat.auggie.airprodiag.com.au/signalr",
        CompanionUrl: "https://companion-uat.auggie.airprodiag.com.au/",
    },
    PRD_AUS: {
        WebApiUrl: "https://api.auggie.airprodiag.com.au/device",
        SignalRUrl: "https://api.auggie.airprodiag.com.au/signalr",
        CompanionUrl: "https://companion.auggie.airprodiag.com.au/",
    },
};

const isEnvKey = (key: string): key is EnvKey => Object.prototype.hasOwnProperty.call(ENVIRONMENT_CONFIG, key);

export class GlobalData {

    public MainApp: any = null!;
    public MainHome: any = null!;
    public MainHeader: any = null!;
    public DeviceSerialNumber: string = "";
    public Environment: string = "PRD";
    public PackageUpgrade: PackageDto | null = null;
    public SupportPhoneNumber: string = import.meta.env.VITE_SupportPhoneNumber || "";

    public BatteryPackageVersion: string = "2025.044.1"; // first package version that supports BatteryInfo
    public TemperaturePackageVersion: string = "2025.220.1"; // first package version that supports TemperatureInfo
    public GyroPackageVersion: string = "2025.224.1"; // first package version that supports Gyro YPR
    public CameraWindowVisibilityVersion: string = "2026.022.1"; // first package version that supports HideCameraWindow/ShowCameraWindow
    public CellularPackageVersion: string = "2026.111.1"; // first package version that supports Auggie.Cellular bindings
    public CenterlinePackageVersion: string = "2026.196.1"; // first package version that supports SetCenterLineActive/CenterLineReached
    public CpuPackageVersion: string = "2026.275.1"; // first package version that supports CpuInfo
    public BottomMarkerPackageVersion: string = "2026.271.1"; // first package version that supports BottomMarkersAvailable

    public GetWebAPIUrl(): string {
        let url = ENVIRONMENT_CONFIG[this.getEnvKey()].WebApiUrl;
        if (!url.endsWith("/"))
            url += "/";
        return url;
    }
    public GetCompanionUrl(): string {
        let url = ENVIRONMENT_CONFIG[this.getEnvKey()].CompanionUrl;
        if (!url.endsWith("/"))
            url += "/";
        return url;
    }
    public GetSignalRFunctionUrl(): string {
        return ENVIRONMENT_CONFIG[this.getEnvKey()].SignalRUrl;
    }
    private getEnvKey(): EnvKey {
        // The environment tier (DEV/QAT/UAT/STG/PRD) is reported by the device client; the region
        // comes from the host, since the AUS device web is served from *.auggie.airprodiag.com.au.
        const region = globalThis.location.hostname.endsWith(".auggie.airprodiag.com.au") ? "_AUS" : "";
        const key = this.Environment + region;
        if (isEnvKey(key))
            return key;
        return region === "_AUS" ? "PRD_AUS" : "PRD";
    }
    public UseFakeCameraImage(): boolean {
        if (AUGGIEAVAILABLE) {
            return Auggie.System.PredefinedCameraImage();
        } else {
            return import.meta.env.VITE_FAKECAMERAIMAGE === "1";
        }
    }
    public IgnoreCameraCalibration(): boolean {
        if (AUGGIEAVAILABLE) {
            return Auggie.System.IgnoreCameraCalibration();
        } else {
            console.log(`VITE_IGNORECAMERACALIBRATION = ${import.meta.env.VITE_IGNORECAMERACALIBRATION}`);
            return import.meta.env.VITE_IGNORECAMERACALIBRATION === "1";
        }
    }
    public get DetailLog(): boolean {
        if (this.DetailLogCache != null) return this.DetailLogCache;
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2023.019.12")) {
            this.DetailLogCache = false;
            return false;
        }
        this.DetailLogCache = Auggie.System.GetDetailLog();
        return this.DetailLogCache || false;
    }
    public set DetailLog(value: boolean) {
        if (!value) return; // we can only turn it on
        if (this.DetailLogCache != null && this.DetailLogCache) return; // already on
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2023.019.12")) return;
        Auggie.System.SetDetailLog(true);
        this.DetailLogCache = true;
    }
    public get UseGyroYPRInd(): boolean {
        if (this.UseGyroYPRIndCache != null) return this.UseGyroYPRIndCache;
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast(this.GyroPackageVersion) || !Auggie.Camera.HasGyro()) {
            this.UseGyroYPRIndCache = false;
        }
        return this.UseGyroYPRIndCache;
    }
    public set UseGyroYPRInd(value: boolean) {
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast(this.GyroPackageVersion)) return;
        Auggie.Camera.SetGyroActive(value);
        this.UseGyroYPRIndCache = Auggie.Camera.GetGyroActive();
        this.DebugGyroYPRInd = Auggie.Camera.GetGyroDebug();
    }
    private DebugGyroYPRInd: boolean = false;

    public get DebugGyroYPR(): boolean {
        return this.DebugGyroYPRInd;
    }

    /**
     * Check whether the installed device client has at least the specified version.
     */
    public IsVersionAtLeast(reqVersion: string): boolean {
        if (!AUGGIEAVAILABLE) return true;
        const deviceVersion = Auggie.System.Version;

        const devParts = deviceVersion.split(".");
        if (devParts.length !== 3)
            return false;
        const deviceVersionMajor = Number(devParts[0]);
        const deviceVersionMinor = Number(devParts[1]);
        const deviceVersionBuild = Number(devParts[2]);

        const reqParts = reqVersion.split(".");
        if (reqParts.length !== 3)
            return false;
        const reqMajor = Number(reqParts[0]);
        const reqMinor = Number(reqParts[1]);
        const reqBuild = Number(reqParts[2]);

        if (reqMajor > deviceVersionMajor) return false;
        if (reqMajor < deviceVersionMajor) return true;
        if (reqMinor > deviceVersionMinor) return false;
        if (reqMinor < deviceVersionMinor) return true;
        if (reqBuild > deviceVersionBuild) return false;
        if (reqBuild < deviceVersionBuild) return true;
        return true;
    }
    public IsVersionBeyondLastSupported(): boolean {
        if (!AUGGIEAVAILABLE) return false;
        const deviceVersion = Auggie.System.Version;

        // The last supported device client version is set by the build pipeline (is overridden in .env file).
        // VITE_LASTSUPPORTEDDEVICEVERSION is set to the current build number in the build pipeline.
        const lastVersion = import.meta.env.VITE_LASTSUPPORTEDDEVICEVERSION || "";
        if (!lastVersion) return false;

        const devParts = deviceVersion.split(".");
        if (devParts.length !== 3)
            return true;
        const deviceVersionMajor = Number(devParts[0]);
        const deviceVersionMinor = Number(devParts[1]);
        const deviceVersionBuild = Number(devParts[2]);

        const lastParts = lastVersion.split(".");
        if (lastParts.length !== 3)
            return true;
        const lastMajor = Number(lastParts[0]);
        const lastMinor = Number(lastParts[1]);
        const lastBuild = Number(lastParts[2]);

        if (lastMajor > deviceVersionMajor) return false;
        if (lastMajor < deviceVersionMajor) return true;
        if (lastMinor > deviceVersionMinor) return false;
        if (lastMinor < deviceVersionMinor) return true;
        if (lastBuild > deviceVersionBuild) return false;
        if (lastBuild < deviceVersionBuild) return true;
        return false;
    }

    public IsPrototype(): boolean {
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2023.186.1")) return false;
        return Auggie.Device.Prototype();
    }

    public IsDemo(): boolean {
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2023.195.1")) return false;
        return Auggie.Device.Demo();
    }

    public IsAir(): boolean {
        if (!AUGGIEAVAILABLE || !Global.IsVersionAtLeast("2026.014.1")) return false;
        return Auggie.Device.DeviceType() === "Air";
    }

    public IsCenterlineSupported(): boolean {
        return Global.IsVersionAtLeast(this.CenterlinePackageVersion);
    }

    public IsBottomMarkerGateSupported(): boolean {
        return Global.IsVersionAtLeast(this.BottomMarkerPackageVersion);
    }

    public DeviceGuid: string = "";
    public DeviceName: string = "";
    public AllowAssistedCalibration: boolean = false;
    public AllowAssistedTargetDisplay: boolean = false;
    public AllowSelfCalibration: boolean = false;
    public DetailLogCache: boolean|null = null;
    public VerifiedVehiclesOnlyInd: boolean = true;
    public CenterlineEnabledInd: boolean = false;
    public CenterlineCompleted: boolean = false;
    // Failed camera starts since the last successful one. A technician-controlled session advances out
    // of camera setup on a timer, so without a budget a client that cannot take the camera over cycles
    // between the two pages for as long as the session lasts.
    public CameraStartFailures: number = 0;
    public UseGyroYPRIndCache: boolean = false;
    public BatteryInfo: BatteryInfoDto|null = null;
    public TemperatureInfo: TemperatureInfoDto|null = null;
    public CpuInfo: CpuInfoDto|null = null;
    public CellularSignal: CellularSignalDto|null = null;
    public CellularInfo: CellularInfoDto|null = null;
    public AmbientLightAlignment: number|null = null;
    public AmbientLightTarget: number|null = null;

    // Session information

    public CalibrationSession: CalibrationSessionDto|null = null;
    public CameraViewerConfiguration: CameraViewerConfiguration|null = null;
    public Make: string = "";
    public Model: string = "";
    public Year: number = 0;
    public Calibration: CalibrationType = CalibrationType.All;
    public LegLengthA: number = 0;
    public LegLengthB: number = 0;
    public LegLengthC: number = 0;
    public Marker: MarkerType = MarkerType.Standard;
    public WheelArchHeightMin: number|null = null;
    public WheelArchHeightMax: number|null = null;
    public TechnicianName: string = "";
    public VehicleId: number|null = null;
    public SessionType: SessionType|null = null;
    public IsTechControlled: boolean = false;
    public LightIntensity: number = 50;
    public OriginalWindshieldAngle: number|null = null;
    public ReportDownloadUrl: string = "";
    public DemoCameraImage: string = "";
    public DemoVehicle: VehicleDto|null = null;

    public clear(): void {
        this.CalibrationSession = null;
        this.CameraViewerConfiguration = null;
        this.Make = "";
        this.Model = "";
        this.Year = 0;
        this.Calibration = CalibrationType.All;
        this.LegLengthA = 0;
        this.LegLengthB = 0;
        this.LegLengthC = 0;
        this.Marker = MarkerType.Standard;
        this.WheelArchHeightMin = null;
        this.WheelArchHeightMax = null;
        this.TechnicianName = "";
        this.VehicleId = null;
        this.SessionType = null;
        this.IsTechControlled = false;
        this.CameraStartFailures = 0;
        this.OriginalWindshieldAngle = null;
        this.ReportDownloadUrl = "";
        this.DemoCameraImage = "";
        this.AmbientLightAlignment = null;
        this.AmbientLightTarget = null;
    }
}
export const Global = new GlobalData();